/**
 * KnowledgeBase Hooks - Custom hooks for article loading
 * 
 * PROJECT_RULES.md Compliance:
 * - ✅ Performance: Optimized data loading
 * - ✅ TypeScript: Fully typed
 */

import { useState, useEffect } from 'react';
import type { ArticleData } from './types';

export const useArticleLoader = (category?: string, article?: string) => {
  const [articleData, setArticleData] = useState<ArticleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        setLoading(true);
        if (!category || !article) {
          throw new Error('Missing category or article');
        }

        // Import the MDX file dynamically
        const path = `/knowledge-base/${category}/${article}.mdx`;
        const response = await fetch(path);
        
        if (!response.ok) {
          throw new Error('Article not found');
        }
        
        const text = await response.text();
        
        // Extract title from the content (first # heading)
        const titleMatch = text.match(/^#\s+(.+)$/m);
        const title = titleMatch ? titleMatch[1] : article.replace(/-/g, ' ');
        
        // Remove the title from content if found
        const content = titleMatch ? text.replace(/^#\s+.+$/m, '').trim() : text;
        
        setArticleData({
          title,
          content,
          category
        });
        setError(null);
      } catch {
        setError('Article not found');
        setArticleData(null);
      } finally {
        setLoading(false);
      }
    };

    if (category && article) {
      loadArticle();
    }
  }, [category, article]);

  return { articleData, loading, error };
};